"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const util_1 = require("../util");
const { POST, PUT, DELETE } = util_1.httpMethods;
/**
 * @classdesc Represents the class interfacing gateway touch related API-I endpoints
 * @class
 */
class GatewayTouch {
    constructor(api) {
        this.api = api;
    }
    /**
     * Drop install a Gateway according to the parameters provided
     *
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#common-nodes-drop-node-post}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!number} args.customerId - Required. The id of customer
     * @param {!object} args.node - Required. The object of the gateway to be installed
     * @param {!number} args.node.propertyId - Required. The property id of the gateway
     * @param {!string} args.node.gatewayIdentifier - Required. The gateway identifier
     * @param {!string} args.node.name - Optional. The name of gateway
     * @param {!string} args.node.location -  Required. The location of the gateway
     * @param {?string[]} args.node.imageUrls - Optional. An array of image urls for the gateway
     * @param {!string} args.node.managementType - Required. Management type of the new node: commercial | research | defunct-retrieve | defunct-abandon | test
     * @param {?number} args.node.maintenanceOwnerId - Required. id of maintenance owner
     * @param {?number} args.node.assetOwnerId - Optional. id of asset owner
     * @param {?string} args.node.desiredFirmware - Optional. The desired firmware version of the gateway. SN only
     * @param {?string} args.antennaType - Optional. The antenna type of the gateway
     * @param {?string} args.powerSource - Optional. The power source of the gateway. SN only
     * @param {?string} args.antennaExtension - Optional. The type of antenna extension used. SN only
     * @param {?object} args.modem - Optional. The modem model. SN only and required if SN.
     * @param {?string} args.modem.modemId - Optional. An array of image urls for the node
     * @param {?string} args.modem.model - Optional. The modem model. SN only and required if SN
     * @param {?string[]} args.modem.modules - Optional. Modem modules. SN only
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return a response object or an error with the problem.
     */
    async installGatewayFromDrop(args) {
        const { customerId, force, node: { propertyId, gatewayIdentifier, name, location, imageUrls, managementType, maintenanceOwnerId, assetOwnerId, desiredFirmware, }, modem: { modemId, model, modules } = {}, powerSource, antennaExtension, antennaType, } = args;
        const methodName = 'installGatewayFromDrop';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'propertyId', propertyId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'propertyId', propertyId, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'gatewayIdentifier', gatewayIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'gatewayIdentifier', gatewayIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'managementType', managementType, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'managementType', managementType, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'maintenanceOwnerId', maintenanceOwnerId, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'assetOwnerId', assetOwnerId, util_1.ValidationTypes.NUMBER),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'desiredFirmware', desiredFirmware, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'powerSource', powerSource, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'antennaExtension', antennaExtension, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'antennaType', antennaType, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'modemId', modemId, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'model', model, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'modules', modules, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'force', force, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(validations);
        const route = '/gateways/drop';
        const input = {
            header: {
                customerId,
            },
            query: {
                force,
            },
            body: {
                customerId,
                force,
                node: {
                    propertyId,
                    gatewayIdentifier,
                    name,
                    location,
                    imageUrls,
                    managementType,
                    maintenanceOwnerId,
                    assetOwnerId,
                    desiredFirmware,
                },
                modem: {
                    modemId,
                    model,
                    modules,
                },
                powerSource,
                antennaExtension,
                antennaType,
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(POST, route, endPointInput);
    }
    /**
     * Remove permanently a gateway according to the parameters provided
     * @see {@link https://noc2-stage.semios.com/api-internal-docs#gateways-remove-gateway-permanently-delete}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string} args.gatewayIdentifier - Required. The gateway identifier being removed
     * @param {!string} args.reason - Required. The reason to remove this gateway
     * @param {!number} args.customerId - Required. The id of customer
     * @param {?string[]} args.imageUrls - Optional. An array of image urls for the gateway
     * @return {promise} Returns a Promise that, when fulfilled, will either return void or an error with the problem.
     */
    async removeGateway(args) {
        const { gatewayIdentifier, customerId, reason, imageUrls } = args || {};
        const methodName = 'removeNode';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'gatewayIdentifier', gatewayIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'gatewayIdentifier', gatewayIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'reason', reason, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'reason', reason, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'imageUrls', imageUrls, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/gateways/${gatewayIdentifier}/remove_permanent`;
        const input = {
            header: {
                customerId,
            },
            body: {
                reason,
                imageUrls,
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(DELETE, route, endPointInput);
    }
    /**
     * Reposition a Gateway according to the parameters provided
     *
     * @see {@link https://api-internal.semios.com/4.0.0/docs?#gateways-reposition-gateway-put|API Call}
     * @param {Object} [args={}] An object containing the arguments accepted by the SDK
     * @param {!string} args.gatewayIdentifier - Required. The gateway identifier being repositioned
     * @param {!string} args.location - Required. WKT of the lat/lng of the node
     * @param {!number} args.customerId - Required. The id of customer
     * @param {?string[]} args.imageUrls - Optional. An array of image urls for the node
     * @return {promise} Assets - Returns a Promise that, when fulfilled, will either return an response object or an error with the problem.
     */
    async repositionGateway(args) {
        const { gatewayIdentifier, location, customerId, imageUrls } = args;
        const methodName = 'repositionGateway';
        // validate
        const validations = [
            (0, util_1.validateParam)(methodName, 'gatewayIdentifier', gatewayIdentifier, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'gatewayIdentifier', gatewayIdentifier, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'location', location, util_1.ValidationTypes.STRING),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.PRESENCE),
            (0, util_1.validateParam)(methodName, 'customerId', customerId, util_1.ValidationTypes.INTEGER),
            (0, util_1.validateParam)(methodName, 'imageUrls', imageUrls, util_1.ValidationTypes.ARRAY, util_1.ValidationTypes.STRING),
        ];
        (0, util_1.validateParams)(validations);
        const route = `/nodes/gtwy/${gatewayIdentifier}/reposition`;
        const input = {
            header: {
                customerId,
            },
            body: {
                location,
                imageUrls,
            },
        };
        const endPointInput = (0, util_1.setFormattedInput)(input);
        return this.api.send(PUT, route, endPointInput);
    }
}
exports.default = GatewayTouch;
//# sourceMappingURL=gateway-touch.js.map